/* ==========================================
   HA Saved + Smart Search (V3) - CLICK-SAFE
   - 3 icons: outfit (same clothing), person (same person), star (save)
   - Saved panel with View / Share / Clear
   - Toast notifications
   - Gallery page support
   - Icons truly clickable even if whole card is a link
   - Saved items open the exact product (store url)
   - Shift+click saved item = search set ID
   - Saved panel placed above filters
   - NO CSS here (moved to ha-icons.css + ha-saved.css)
   ========================================== */

(function () {
  try {
    // Skip homepage and search pages
    if (location.pathname === "/") {
      console.log("🧾 HA Saved: skipped (home page) 🏠");
      return;
    }
    if (location.pathname === "/search" || location.pathname === "/a/search") {
      console.log("🧾 HA Saved: skipped (search page) 🔍");
      return;
    }

    console.log("🧾 HA Saved + Smart Search init ✅");

    const CFG = {
      searchInputSelectors: [
        "input.cloud-search-text-field__input",
        ".cloud-search-text-field input[type='search']",
        "input[type='search'][placeholder*='Search']"
      ],
      cardTitleSelectors: [
        "#product-grid .card__heading a.full-unstyled-link",
        "#product-grid .card__heading",
        ".card__heading a.full-unstyled-link"
      ],
      sidebarSelectors: [
        "#cloud-search-filters-sidebar__filters",
        ".cloud-search-filters-sidebar",
        "[id*='cloud-search-filters-sidebar']"
      ],
      storageKey: "ha_saved_models_v1",
      panelId: "ha-saved-panel",
      galleryPath: "/pages/saved-gallery"
    };

    function qAny(selectors, root = document) {
      for (const s of selectors) {
        const el = root.querySelector(s);
        if (el) return el;
      }
      return null;
    }

    function getSearchInput() {
      return qAny(CFG.searchInputSelectors);
    }

    function pushSearch(value) {
      const input = getSearchInput();
      if (!input) {
        console.warn("🧾 HA Saved: search input not found ❌");
        return;
      }

      input.value = value;

      // Standard typing events
      input.dispatchEvent(new Event("input", { bubbles: true }));
      input.dispatchEvent(new Event("change", { bubbles: true }));

      // Simulate Enter key
      const enterDown = new KeyboardEvent("keydown", {
        bubbles: true,
        cancelable: true,
        key: "Enter",
        code: "Enter",
        keyCode: 13
      });

      const enterUp = new KeyboardEvent("keyup", {
        bubbles: true,
        cancelable: true,
        key: "Enter",
        code: "Enter",
        keyCode: 13
      });

      input.dispatchEvent(enterDown);
      input.dispatchEvent(enterUp);

      // If input is inside a form, submit it
      const form = input.closest("form");
      if (form) {
        form.dispatchEvent(new Event("submit", { bubbles: true, cancelable: true }));
      }

      console.log("🧾 HA Saved: pushed search + enter 🔎", value);
    }

    function parseCardTitle(fullRaw) {
      const full = (fullRaw || "").trim();
      const m = full.match(/^(\d{5})_([^\d_]+)\d*/);
      if (m) return { id: m[1], person: m[2], label: full };

      const parts = full.split("_");
      const id = (parts[0] || "").slice(0, 5);
      const person = (parts[1] || full).replace(/\d+$/g, "");
      return { id, person, label: full };
    }

    function loadSaved() {
      try {
        const raw = localStorage.getItem(CFG.storageKey);
        const arr = JSON.parse(raw || "[]");
        return Array.isArray(arr) ? arr : [];
      } catch {
        return [];
      }
    }

    function saveSaved(list) {
      localStorage.setItem(CFG.storageKey, JSON.stringify(list));
    }

    function isSaved(label) {
      return loadSaved().some((x) => x.label === label);
    }

    function toggleSaved(item) {
      let list = loadSaved();
      const exists = list.find((x) => x.label === item.label);
      if (exists) list = list.filter((x) => x.label !== item.label);
      else list.push(item);

      saveSaved(list);
      renderSavedPanel();
      refreshStarStates();
    }

    // 🔥 block navigation/listeners in CAPTURE phase.
    function hardBlockEvent(e) {
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === "function") e.stopImmediatePropagation();
      e.returnValue = false;
      return false;
    }

    function bindHardBlock(el, handler) {
      const onPointerDown = (e) => {
        hardBlockEvent(e);
      };

      const onClick = (e) => {
        hardBlockEvent(e);
        handler(e); // 👈 pass event for shiftKey etc.
      };

      el.addEventListener("pointerdown", onPointerDown, { capture: true });
      el.addEventListener("mousedown", onPointerDown, { capture: true });
      el.addEventListener("touchstart", onPointerDown, { capture: true, passive: false });
      el.addEventListener("click", onClick, { capture: true });
    }

    // ========================
    // SVG Icons
    // ========================

    function svgOutfit() {
      return `
        <svg viewBox="0 0 24 24" class="ha-svg">
          <path d="M7 4l5-2 5 2 2 4-3 2v10H8V10L5 8l2-4z"/>
        </svg>
      `;
    }

    function svgPerson() {
      return `
        <svg viewBox="0 0 24 24" class="ha-svg">
          <circle cx="12" cy="8" r="4"/>
          <path d="M4 22c0-4 4-6 8-6s8 2 8 6"/>
        </svg>
      `;
    }

    function svgStar() {
      // simple solid star (SVG so it can be forced white by CSS)
      return `
        <svg viewBox="0 0 24 24" class="ha-svg">
          <path d="M12 2.5l2.9 6 6.6.6-5 4.3 1.6 6.5-6.1-3.4-6.1 3.4 1.6-6.5-5-4.3 6.6-.6L12 2.5z"/>
        </svg>
      `;
    }

    function createIcon(svgMarkup, title, handler) {
      const span = document.createElement("span");
      span.innerHTML = svgMarkup;
      span.title = title;
      span.className = "ha-icon";
      span.setAttribute("role", "button");
      span.setAttribute("tabindex", "0");

      bindHardBlock(span, handler);

      span.addEventListener(
        "keydown",
        (e) => {
          if (e.key === "Enter" || e.key === " ") {
            hardBlockEvent(e);
            handler(e);
          }
        },
        { capture: true }
      );

      return span;
    }

    // ========================
    // Toast notification
    // ========================

    function showToast(message, durationMs) {
      durationMs = durationMs || 2500;
      // Remove existing toast
      const old = document.getElementById("ha-toast");
      if (old) old.remove();

      const toast = document.createElement("div");
      toast.id = "ha-toast";
      toast.textContent = message;
      document.body.appendChild(toast);

      // Trigger animation
      requestAnimationFrame(() => {
        toast.classList.add("ha-toast-show");
      });

      setTimeout(() => {
        toast.classList.remove("ha-toast-show");
        setTimeout(() => toast.remove(), 400);
      }, durationMs);
    }

    // ========================
    // Gallery URL helpers
    // ========================

    function buildGalleryUrl() {
      const list = loadSaved();
      if (!list.length) return null;

      // Build handles from labels: "00086_Ewoud001" -> "00086-ewoud001"
      const handles = list.map((item) => {
        // Try to get handle from url first
        if (item.url) {
          const match = item.url.match(/\/products\/([^/?#]+)/);
          if (match) return match[1];
        }
        // Fallback: convert label to handle format
        return item.label.toLowerCase().replace(/_/g, "-");
      });

      return CFG.galleryPath + "?list=" + handles.join(",");
    }

    // ========================
    // Sidebar & Panel
    // ========================

    function findSidebarContainer() {
      return qAny(CFG.sidebarSelectors);
    }

    function getCardProductUrlFromTitleEl(titleEl) {
      const card = titleEl?.closest?.(".card-wrapper");
      if (!card) return null;

      const a =
        card.querySelector("a[href^='/products/']") ||
        card.querySelector("a[href*='/products/']") ||
        card.querySelector("a.full-unstyled-link");

      return a ? a.getAttribute("href") : null;
    }

    function ensurePanel(sidebar) {
      let panel = document.getElementById(CFG.panelId);
      if (panel) return panel;

      panel = document.createElement("div");
      panel.id = CFG.panelId;
      panel.innerHTML = `
        <div class="ha-saved-title">
          Saved ⭐
          <span class="ha-saved-help" title="Click = open product&#10;Shift+click = same outfit&#10;Ctrl/Cmd+click = more from this person">?</span>
        </div>

        <div class="ha-saved-list"></div>
        <div class="ha-saved-actions">
          <button type="button" class="ha-view-saved">👁️ View</button>
          <button type="button" class="ha-share-saved">🔗 Share</button>
          <button type="button" class="ha-clear">🗑️ Clear</button>
        </div>
      `;

      // Put at TOP of sidebar (above filters)
      sidebar.insertBefore(panel, sidebar.firstChild);

      // --- View Saved button ---
      panel.querySelector(".ha-view-saved").addEventListener(
        "click",
        (e) => {
          hardBlockEvent(e);
          const url = buildGalleryUrl();
          if (!url) {
            showToast("No saved models yet!");
            return;
          }
          window.location.href = url;
        },
        { capture: true }
      );

      // --- Share button ---
      panel.querySelector(".ha-share-saved").addEventListener(
        "click",
        async (e) => {
          hardBlockEvent(e);
          const url = buildGalleryUrl();
          if (!url) {
            showToast("No saved models to share!");
            return;
          }
          const fullUrl = window.location.origin + url;
          try {
            await navigator.clipboard.writeText(fullUrl);
            showToast("🔗 Link copied! Share it with anyone.");
            console.log("🧾 HA Saved: copied share URL ✅", fullUrl);
          } catch (err) {
            // Fallback: select a temporary input
            const tmp = document.createElement("input");
            tmp.value = fullUrl;
            document.body.appendChild(tmp);
            tmp.select();
            document.execCommand("copy");
            tmp.remove();
            showToast("🔗 Link copied!");
          }
        },
        { capture: true }
      );

      // --- Clear button ---
      panel.querySelector(".ha-clear").addEventListener(
        "click",
        (e) => {
          hardBlockEvent(e);
          const count = loadSaved().length;
          saveSaved([]);
          renderSavedPanel();
          refreshStarStates();
          showToast("🗑️ Cleared " + count + " saved model" + (count !== 1 ? "s" : ""));
        },
        { capture: true }
      );

      return panel;
    }

    function renderSavedPanel() {
      const sidebar = findSidebarContainer();
      if (!sidebar) return;

      const panel = ensurePanel(sidebar);
      const listEl = panel.querySelector(".ha-saved-list");
      const list = loadSaved();

      listEl.innerHTML = "";

      if (!list.length) {
        const empty = document.createElement("div");
        empty.className = "ha-empty";
        empty.textContent = "Nog leeg. Klik \u2605 op een card om te saven.";
        listEl.appendChild(empty);
        return;
      }

      for (const item of list) {
        const row = document.createElement("div");
        row.className = "ha-saved-row";

        // Add remove button
        const removeBtn = document.createElement("span");
        removeBtn.className = "ha-saved-remove";
        removeBtn.textContent = "\u00D7";
        removeBtn.title = "Remove";

        const label = document.createElement("b");
        label.textContent = item.label;

        row.appendChild(label);
        row.appendChild(removeBtn);

        // Remove button click
        bindHardBlock(removeBtn, () => {
          let current = loadSaved();
          current = current.filter((x) => x.label !== item.label);
          saveSaved(current);
          renderSavedPanel();
          refreshStarStates();
          showToast("Removed " + item.label);
        });

        // Row click: navigate or search
        bindHardBlock(row, (e) => {
          // Don't navigate if clicking remove button
          if (e && e.target && e.target.closest(".ha-saved-remove")) return;

          // Ctrl/Cmd+click: more from same person
          if (e && (e.ctrlKey || e.metaKey)) {
            if (item.person) pushSearch(item.person);
            else pushSearch(item.id); // fallback
            return;
          }

          // Shift+click: same outfit (set id)
          if (e && e.shiftKey) {
            pushSearch(item.id);
            return;
          }

          // Normal click: open exact product
          if (item.url) {
            window.location.href = item.url;
            return;
          }

          // Fallback for older saved items that have no url yet
          pushSearch(item.id);
        });

        listEl.appendChild(row);
      }
    }

    // ========================
    // Card enhancement (3 icons)
    // ========================

    function enhanceCards(root) {
      root = root || document;
      const nodes = [];
      for (const sel of CFG.cardTitleSelectors) nodes.push(...root.querySelectorAll(sel));

      nodes.forEach((nameEl) => {
        const anchor = nameEl.matches("a") ? nameEl : nameEl.querySelector("a");
        const target = anchor || nameEl;

        // Find the card-wrapper and card__content
        const cardWrapper = target.closest(".card-wrapper");
        if (!cardWrapper) return;
        
        // Check if already enhanced
        if (cardWrapper.dataset.haEnhanced === "1") return;
        cardWrapper.dataset.haEnhanced = "1";

        const full = (target.textContent || "").trim();
        if (!full) return;

        const parsed = parseCardTitle(full);
        const productUrl = getCardProductUrlFromTitleEl(target);

        // Create as a div instead of span for better block behavior
        const wrapper = document.createElement("div");
        wrapper.className = "ha-tools";
        wrapper.dataset.haLabel = parsed.label;

        // Icon 1: Same clothing, different poses (search by set ID)
        wrapper.appendChild(
          createIcon(svgOutfit(), "Same clothing, different poses", () => pushSearch(parsed.id))
        );

        // Icon 2: More of the same person (search by person name)
        wrapper.appendChild(
          createIcon(svgPerson(), "More of the same person", () => pushSearch(parsed.person))
        );

        // Icon 3: Save / unsave (star)
        const star = createIcon(svgStar(), "Save", () =>
          toggleSaved({ label: parsed.label, id: parsed.id, person: parsed.person, url: productUrl })
        );
        if (isSaved(parsed.label)) star.classList.add("ha-star-on");

        wrapper.appendChild(star);
        
// Find the OUTER card__information (the one with price, not the overlay)
        // There are TWO card__content divs: one inside card__inner (overlay) and one outside (footer)
        const allInfo = cardWrapper.querySelectorAll(".card__information");
        const targetInfo = allInfo.length > 1 ? allInfo[allInfo.length - 1] : allInfo[0];
        if (targetInfo) {
          targetInfo.appendChild(wrapper);
        } else {
          cardWrapper.appendChild(wrapper);
        }
      });
    }

    function refreshStarStates() {
      const saved = new Set(loadSaved().map((x) => x.label));

      // Find all ha-tools and check their label
      document.querySelectorAll(".ha-tools[data-ha-label]").forEach((tools) => {
        const label = tools.dataset.haLabel;
        const icons = tools.querySelectorAll(".ha-icon");
        if (icons.length) {
          const star = icons[icons.length - 1]; // last icon is the star
          star.classList.toggle("ha-star-on", saved.has(label));
        }
      });
    }

    // ========================
    // Boot
    // ========================

    let _gridObserver = null;

    function boot() {
      enhanceCards();
      renderSavedPanel();

      // Always start the sidebar waiter too, in case sidebar loads late
      haWaitForSidebarAndRender();

      const grid = document.querySelector("#product-grid");
      const observeTarget = grid || document.body;

      // Only create one MutationObserver
      if (!_gridObserver) {
        _gridObserver = new MutationObserver((mutations) => {
          for (const m of mutations) {
            if (m.addedNodes && m.addedNodes.length) {
              enhanceCards(observeTarget);
              break;
            }
          }
          renderSavedPanel();
        });
        _gridObserver.observe(observeTarget, { childList: true, subtree: true });
      }

      console.log("🧾 HA Saved: booted ✅", {
        searchInput: !!getSearchInput(),
        sidebar: !!findSidebarContainer(),
        grid: !!grid
      });
    }

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", boot);
    } else {
      boot();
    }

    // ✅ SAFE SIDEBAR WAITER
    // Polls until the CloudSearch sidebar appears (it loads async / late after back-button)
    // Then renders the saved panel + enhances any new cards
    let _waiterRunning = false;

    function haWaitForSidebarAndRender() {
      if (_waiterRunning) return; // don't stack multiple waiters
      _waiterRunning = true;

      const start = Date.now();
      const maxMs = 12000;     // 12s max wait
      const intervalMs = 200;  // check every 200ms
      let panelRendered = false;

      const t = setInterval(() => {
        try {
          const sidebar = findSidebarContainer();
          const grid = document.querySelector("#product-grid");
          const panelExists = document.getElementById(CFG.panelId);

          // If sidebar is there but panel isn't rendered yet → render it now
          if (sidebar && !panelExists) {
            enhanceCards(document);
            renderSavedPanel();
            panelRendered = true;
            console.log("🧾 HA waiter: sidebar found, panel rendered ✅ (" + (Date.now() - start) + "ms)");
          }

          // Also re-enhance cards if grid appeared
          if (grid) {
            enhanceCards(document);
          }

          // If we've rendered the panel, or we've been waiting long enough, stop
          if (panelRendered || (sidebar && panelExists)) {
            clearInterval(t);
            _waiterRunning = false;
            return;
          }
        } catch (e) {
          console.warn("🧾 HA waiter pass failed:", e);
        }

        if (Date.now() - start > maxMs) {
          clearInterval(t);
          _waiterRunning = false;
          console.warn("🧾 HA waiter timeout ⚠️");
        }
      }, intervalMs);
    }

    document.addEventListener("shopify:section:load", boot);

    // Back/forward button: sidebar gets destroyed and recreated
    window.addEventListener("popstate", () => {
      // Multiple retries at different delays to catch the sidebar
      [100, 400, 800, 1500, 3000].forEach((delay) => {
        setTimeout(() => {
          const panelExists = document.getElementById(CFG.panelId);
          if (!panelExists) {
            console.log("🧾 HA popstate retry at " + delay + "ms");
            enhanceCards(document);
            renderSavedPanel();
            haWaitForSidebarAndRender();
          }
        }, delay);
      });
    });
  } catch (err) {
    console.error("🧾 HA Saved + Smart Search FATAL:", err);
  }
  
})();